import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { SettingsProvider } from './contexts/SettingsContext';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import Transactions from './components/Transactions';
import TransactionForm from './components/TransactionForm';
import Members from './components/Members';
import Customers from './components/Customers';
import Vendors from './components/Vendors';
import Invoices from './components/Invoices';
import InvoiceForm from './components/InvoiceForm';
import PurchaseOrders from './components/PurchaseOrders';
import POForm from './components/POForm';
import Reports from './components/Reports';
import Settings from './components/Settings';
import BrandLanding from './components/BrandLanding';
import LoadingScreen from './components/LoadingScreen';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import UserManagement from './components/UserManagement';
import AuditLogs from './components/AuditLogs';
import { AuthProvider } from './contexts/AuthContext';

// MUI Theme - Mobile First
const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#2e7d32', // Green for agriculture theme
            light: '#60ad5e',
            dark: '#005005',
        },
        secondary: {
            main: '#ff6f00', // Orange accent
            light: '#ffa040',
            dark: '#c43e00',
        },
        background: {
            default: '#f5f7fa',
            paper: '#ffffff',
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontSize: 13,
        h4: {
            fontSize: '1.6rem',
            fontWeight: 600,
        },
        h5: {
            fontSize: '1.3rem',
            fontWeight: 600,
        },
        h6: {
            fontSize: '1.05rem',
            fontWeight: 600,
        },
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 8,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '8px 10px',
                    fontSize: '0.78rem',
                    lineHeight: 1.35,
                },
                head: {
                    fontWeight: 600,
                    backgroundColor: '#f5f7fa',
                    whiteSpace: 'nowrap',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRight: 'none',
                    boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
                },
            },
        },
    },
});

function App() {
    console.log('App component rendering...');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Show splash screen for 4 seconds to reinforce multi-brand sequence
        const timer = setTimeout(() => {
            setLoading(false);
        }, 4000);

        return () => clearTimeout(timer);
    }, []);

    if (loading) {
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <LoadingScreen />
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <SettingsProvider>
                <AuthProvider>
                    <Router>
                        <Routes>
                            <Route path="/login" element={<Login />} />
                            <Route path="/branding" element={<BrandLanding />} />

                            {/* Protected Routes */}
                            <Route element={<Layout><ProtectedRoute /></Layout>}>
                                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                                <Route path="/dashboard" element={<Dashboard />} />
                                <Route path="/transactions" element={<Transactions />} />
                                <Route path="/transactions/new" element={<TransactionForm />} />
                                <Route path="/transactions/:id" element={<TransactionForm />} />
                                <Route path="/members" element={<Members />} />
                                <Route path="/customers" element={<Customers />} />
                                <Route path="/vendors" element={<Vendors />} />
                                <Route path="/invoices" element={<Invoices />} />
                                <Route path="/invoices/new" element={<InvoiceForm />} />
                                <Route path="/invoices/:id" element={<InvoiceForm />} />
                                <Route path="/purchase-orders" element={<PurchaseOrders />} />
                                <Route path="/purchase-orders/new" element={<POForm />} />
                                <Route path="/purchase-orders/:id" element={<POForm />} />
                                <Route path="/reports" element={<Reports />} />
                                <Route path="/settings" element={<Settings />} />
                            </Route>

                            {/* Admin Only Routes */}
                            <Route element={<Layout><ProtectedRoute roles={['ADMIN']} /></Layout>}>
                                <Route path="/users" element={<UserManagement />} />
                                <Route path="/audit-logs" element={<AuditLogs />} />
                            </Route>

                            <Route path="*" element={<Navigate to="/login" replace />} />
                        </Routes>
                    </Router>
                </AuthProvider>
            </SettingsProvider>
            <ToastContainer
                position="bottom-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />

        </ThemeProvider >
    );
}

export default App;
